import { Button, ButtonGroup, DeleteIcon, EditIcon, Modal, Row, Table, TableColumns } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useBuildingsListQuery, useDeleteBuildingsMutation } from '../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserErrorToast } from '../../../utils/toast';
import { BuildingsForm } from '../Forms/BuildingsForm';
import { BuildingsListType } from '../graphgl/types';

export const TimetableBuildingsTable: FC = observer(() => {
  const { pimAuthClaims } = useAuthClaims();
  const { t } = useTranslation();
  const currentSchoolYear = useSelectedSchoolYear();
  const permissions = usePermissionChecker();
  const readonly = !permissions?.canWriteTimetable();

  const context = useMemorizedCacheTag('BUILDINGS');
  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('timetable-buildings-list');
  const { sorting, saveSorting } = useColumnsSort('timetable-buildings-list');

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [buildingUuid, setBuildingUuid] = useState<string | null>(null);

  const [, deleteBuilding] = useDeleteBuildingsMutation();

  const [{ data }] = useBuildingsListQuery({ context });

  const memoizedData = useMemo((): BuildingsListType[] => {
    return data && data ? data.buildings : [];
  }, [data]);

  function tableColumns(): TableColumns<BuildingsListType>[] {
    return [
      {
        header: t('common.name') as string,
        id: 'name',
        accessorKey: 'name',
        size: 450,
      },
      {
        header: t('common.description') as string,
        id: 'description',
        accessorKey: 'description',
        size: 450,
        canExpand: true,
      },
      {
        header: t('common.active.full') as string,
        accessorKey: 'active',
        id: 'active',
        type: 'active',
      },
    ];
  }

  const columns = useMemo(tableColumns, []);

  const onModalClose = () => {
    setBuildingUuid(null);
    setIsModalOpen(false);
  };

  const handleEdit = (buildingsRow: Row<BuildingsListType>) => {
    setBuildingUuid(buildingsRow.original.uuid);
    setIsModalOpen(true);
  };

  async function handleDelete(row: Row<BuildingsListType>) {
    if (!row.original.uuid) {
      return;
    }
    const response = await deleteBuilding(
      {
        uuid: row.original.uuid,
      },
      context,
    );

    if (response.error) {
      showUserErrorToast({ error: response.error });
    } else {
      showSuccessDeleteToast([row.original.name]);
    }
  }

  return (
    <>
      <Table<BuildingsListType>
        showBorderRadius
        showShadow
        canScroll
        minHeight={600}
        columns={columns}
        data={memoizedData}
        sorting={sorting}
        onSortingChange={saveSorting}
        showActionBar
        actionBarSettings={{
          showAddButton: true,
          disableAddButton: readonly,
        }}
        isOnWhite={false}
        showVisibility
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('buildings.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
          filename: `${t('buildings.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
        }}
        showSelect={false}
        onAddClick={() => {
          setIsModalOpen(true);
        }}
        lastColWidth='80px'
        lastCol={(row) => {
          return (
            <ButtonGroup disabled={readonly}>
              <Button
                hierarchy='secondary'
                type='button'
                onClick={() => handleEdit(row)}
                icon={<EditIcon className='small' />}
                disabled={readonly}
              />
              <Button
                hierarchy='secondary'
                type='button'
                onClick={() => handleDelete(row)}
                icon={<DeleteIcon className='small' />}
                disabled={readonly}
              />
            </ButtonGroup>
          );
        }}
      />
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onRequestClose={onModalClose}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          title={buildingUuid ? (t('buildings.edit') as string) : (t('buildings.add') as string)}
        >
          <BuildingsForm handleClose={onModalClose} buildingUuid={buildingUuid} />
        </Modal>
      )}
    </>
  );
});
